import { invariant } from '@seen/core/invariant'
import { ROLE_OPTIONS_WITH_KEYWORDS } from '@seen/core/models/care-teams/constants'
import { RecentSelections } from '@seen/core/models/recentSelections'
import { TaskLabel } from '@seen/core/models/taskLabel/taskLabel'
import { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import { TaskView } from '@seen/core/models/taskView'
import { Button, cn, IconUser, TypographySubtle } from '@seen/ui'
import {
  IconBuilding,
  IconChevronDown,
  IconEye,
  IconProgress,
  IconSearch,
  IconSortAscending,
  IconSortDescending,
} from '@tabler/icons-react'
import { compact, isEmpty, isEqual, omit } from 'lodash-es'
import pluralize from 'pluralize'
import type * as React from 'react'
import { type JSX, type ReactNode, useCallback, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'
import { create } from 'zustand'
import { combine, devtools, persist } from 'zustand/middleware'
import { ComboboxOption } from '#src/components/ComboboxOption.tsx'
import { Avatar } from '#src/components/core/Avatar/Avatar.tsx'
import { BulkActionBar } from '#src/components/core/BulkActionBar.tsx'
import { Checkbox } from '#src/components/core/Checkbox.tsx'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogButton } from '#src/components/core/dialog/DialogButton.tsx'
import { DialogClose } from '#src/components/core/dialog/DialogClose.tsx'
import { DialogFooter } from '#src/components/core/dialog/DialogFooter.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { Form } from '#src/components/core/form/Form.tsx'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { SubmitButton } from '#src/components/core/form/SubmitButton.tsx'
import { Spinner } from '#src/components/core/Loader.tsx'
import { Popover } from '#src/components/core/popover/Popover.tsx'
import { PopoverContent } from '#src/components/core/popover/PopoverContent.tsx'
import { PopoverTrigger } from '#src/components/core/popover/PopoverTrigger.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { VirtualizedList } from '#src/components/core/VirtualizedList.tsx'
import { DisplayButton } from '#src/components/DisplayButton.tsx'
import type { Filter, FilterConfigMap } from '#src/components/filter/Filter.tsx'
import { FilterEditor } from '#src/components/filter/FilterEditor.tsx'
import {
  FilterValue,
  useFilteredData,
} from '#src/components/filter/useFilteredData.ts'
import { Helmet } from '#src/components/Helmet.tsx'
import { IconLabel } from '#src/components/icons/IconLabel.tsx'
import { IconTaskState } from '#src/components/icons/IconTaskState.tsx'
import { IconUserPlaceholder } from '#src/components/icons/IconUserPlaceholder.tsx'
import { LabelColor } from '#src/components/LabelColor.tsx'
import { LabelOption } from '#src/components/LabelOption.tsx'
import { ListHeader } from '#src/components/ListHeader.tsx'
import { PractitionerNameplate } from '#src/components/nameplates/PractitionerNameplate.tsx'
import { ParticipantOption } from '#src/components/options/ParticipantOption.tsx'
import { PractitionerOption } from '#src/components/options/PractitionerOption.tsx'
import { OwnerComboboxButton } from '#src/components/OwnerComboboxButton.tsx'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { TaskListRow } from '#src/components/task/list/TaskListRow.tsx'
import {
  priorityDefinition,
  TaskPriorityOption,
} from '#src/components/task/TaskPriorityCombobox.tsx'
import { TaskPriorityIcon } from '#src/components/task/TaskPriorityIcon.tsx'
import { TaskStateCombobox } from '#src/components/task/TaskStateCombobox.tsx'
import { TaskStateText } from '#src/components/task/TaskStateText.tsx'
import { ViewRow } from '#src/components/ViewRow.tsx'
import {
  createTaskFilter,
  useMarkRelatedInboxItemsAsDone,
} from '#src/hooks/inbox/index.ts'
import { useConfirmationStore } from '#src/hooks/useConfirmationStore.ts'
import { useCreateMutation } from '#src/hooks/useCreateMutation.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useBatchMutation } from '#src/hooks/useFhirBundleMutation.ts'
import { useForm } from '#src/hooks/useForm.ts'
import { useHotkeys } from '#src/hooks/useHotkeys.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useParticipants } from '#src/hooks/useParticipants.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { usePractitionersInternal } from '#src/hooks/usePractitionersInternal.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { useSearch } from '#src/hooks/useSearch.ts'
import { useTaskComposerStore } from '#src/hooks/useTaskComposerStore.ts'
import {
  type TaskListGrouping,
  type TaskListSort,
  type TaskListSortField,
  useTaskListItems,
} from '#src/hooks/useTaskListItems.ts'
import { useTasks } from '#src/hooks/useTasks.ts'

const EMPTY_FILTER_VIEW: Filter[] = []

const TASK_SORT_FIELDS: {
  label: string
  value: TaskListSortField
  keywords: string[]
}[] = [
  {
    label: 'Priority',
    value: 'priority',
    keywords: ['priority', 'urgent', 'stat', 'default'],
  },
  {
    label: 'Created',
    value: 'authoredOn',
    keywords: ['created', 'authored', 'task created'],
  },
  {
    label: 'Due date',
    value: 'dueOn',
    keywords: ['due', 'due date'],
  },
  {
    label: 'Last updated',
    value: 'lastUpdated',
    keywords: ['last updated', 'recent', 'updated'],
  },
  {
    label: 'Title',
    value: 'title',
    keywords: ['title', 'name', 'alphabetical'],
  },
]

const TASK_DISPLAY_PROPERTIES: {
  key: keyof TasksPageDisplay
  label: string
}[] = [
  { key: 'showPriority', label: 'Priority' },
  { key: 'showLabels', label: 'Labels' },
  { key: 'showDueDate', label: 'Due date' },
  { key: 'showAuthoredOn', label: 'Created' },
  { key: 'showOwner', label: 'Owner' },
]

export function TasksPage(): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const currentPractitioner = useCurrentPractitioner()

  const { data: taskViews } = useSearch(
    TaskView,
    TaskView.queryForPractitioner(currentPractitioner.reference)
  )

  const selectedView = useTasksPageStore((state) => state.view)

  const storedFilters = useTasksPageStore(
    (state) => state.filtersByView[state.view]
  )

  const changeView = useTasksPageStore((state) => state.changeView)

  const updateCurrentFilters = useTasksPageStore(
    (state) => state.updateCurrentFilters
  )

  const storedGrouping = useTasksPageStore(
    (state) => state.groupingByView[state.view]
  )
  const updateCurrentGrouping = useTasksPageStore(
    (state) => state.updateCurrentGrouping
  )

  const sort = useTasksPageStore((state) => state.sort)
  const updateCurrentSort = useTasksPageStore(
    (state) => state.updateCurrentSort
  )
  const unsetSort = useTasksPageStore((state) => state.unsetSort)

  const storedDisplay = useTasksPageStore(
    (state) => state.displayByView[state.view]
  )
  const setDisplay = useTasksPageStore((state) => state.setDisplay)
  const unsetDisplay = useTasksPageStore((state) => state.unsetDisplay)
  const currentDisplay = useMemo<TasksPageDisplay>(
    () => ({ ...DEFAULT_DISPLAY, ...storedDisplay }),
    [storedDisplay]
  )
  const isDisplayDirty = useMemo(
    () =>
      TASK_DISPLAY_PROPERTIES.some(
        ({ key }) => currentDisplay[key] !== DEFAULT_DISPLAY[key]
      ),
    [currentDisplay]
  )
  const isSortDirty = useMemo(() => !isEqual(sort, DEFAULT_SORT), [sort])

  const defaultAssignedFilters = useMemo(
    () => [
      {
        field: 'owner',
        operator: 'in' as const,
        value: [currentPractitioner.reference.reference],
      },
    ],
    [currentPractitioner.reference.reference]
  )

  const defaultCreatedFilters = useMemo(
    () => [
      {
        field: 'requester',
        operator: 'in' as const,
        value: [currentPractitioner.reference.reference],
      },
    ],
    [currentPractitioner.reference.reference]
  )

  const taskViewFilterString = useMemo(() => {
    const taskView = taskViews?.find(
      (v) => v.reference.reference === selectedView
    )
    return taskView?.filters
  }, [taskViews, selectedView])

  const viewFilters = useMemo(() => {
    if (taskViewFilterString) {
      return JSON.parse(taskViewFilterString) as Filter[]
    }
    if (selectedView === 'assigned') {
      return defaultAssignedFilters
    }
    if (selectedView === 'created') {
      return defaultCreatedFilters
    }
    // If selectedView is ALL or not found, don't apply any filters.
    return EMPTY_FILTER_VIEW
  }, [
    taskViewFilterString,
    defaultAssignedFilters,
    defaultCreatedFilters,
    selectedView,
  ])

  const viewGrouping = useMemo<TaskListGrouping>(() => {
    const taskView = taskViews?.find(
      (v) => v.reference.reference === selectedView
    )
    return taskView?.grouping ?? 'state'
  }, [taskViews, selectedView])

  const filters = storedFilters ?? viewFilters
  const grouping = storedGrouping ?? viewGrouping

  // FilterEditor passes functional updaters that read `prev`. Resolve against
  // the derived effective filters so the first edit on a fresh view doesn't
  // wipe out the view defaults the user can see.
  const setFilters = useCallback(
    (next: Filter[] | ((prev: Filter[]) => Filter[])) => {
      updateCurrentFilters(typeof next === 'function' ? next(filters) : next)
    },
    [filters, updateCurrentFilters]
  )

  const isGroupingChanged = useMemo(
    () => !isEqual(grouping, viewGrouping),
    [grouping, viewGrouping]
  )

  const canResetFilters = useMemo(
    () => !isEqual(filters, viewFilters) || isGroupingChanged,
    [filters, viewFilters, isGroupingChanged]
  )

  // Derive opt-in states (done/canceled/duplicate) from the active status
  // filter so we only fetch them when the user explicitly filters by them.
  const optInStates = useMemo<StandardTask['state'][]>(() => {
    const statusFilter = filters.find((f) => f.field === 'status')
    if (!statusFilter?.value?.length) {
      return []
    }
    return statusFilter.value.filter((v): v is StandardTask['state'] =>
      (['done', 'canceled', 'duplicate'] as const).some((s) => s === v)
    )
  }, [filters])

  const { data: tasks, isLoading } = useTasks(optInStates)

  const participants = useParticipants()

  const participantOptions = useMemo(
    () =>
      participants.map((p) => ({
        label: <ParticipantOption participant={p.reference} />,
        value: p.reference.reference,
        icon: <Avatar profile={p.reference} size={3.5} />,
        keywords: p.searchKeywords,
      })),
    [participants]
  )

  const practitioners = usePractitionersInternal()

  const practitionerOptions = useMemo(
    () => [
      {
        label: (
          <ComboboxOption>
            <IconUser className="size-5" /> Current user
          </ComboboxOption>
        ),
        selectedLabel: (
          <div className="flex items-center gap-1">
            <IconUser className="size-3.5" /> Current user
          </div>
        ),
        value: FilterValue.CurrentUser,
        icon: <IconUser className="size-3.5" />,
        keywords: ['Current user'],
      },
      ...practitioners.map((p) => ({
        label: <PractitionerOption practitioner={p.reference} />,
        selectedLabel: (
          <PractitionerNameplate practitioner={p.reference} size={3.5} />
        ),
        value: p.reference.reference,
        icon: <Avatar profile={p.reference} size={3.5} />,
        keywords: p.searchKeywords,
      })),
    ],
    [practitioners]
  )

  const ownerOptions = useMemo(
    () => [
      {
        label: (
          <ComboboxOption>
            <IconUserPlaceholder className="size-5 p-0" /> No owner
          </ComboboxOption>
        ),
        selectedLabel: (
          <div className="flex items-center gap-1">
            <IconUserPlaceholder className="size-3.5 p-0" /> No owner
          </div>
        ),
        value: FilterValue.None,
        icon: <IconUserPlaceholder className="size-3.5 p-0" />,
        keywords: ['No owner'],
      },
      ...practitionerOptions,
    ],
    [practitionerOptions]
  )

  const { data: labels } = useSearch(TaskLabel, TaskLabel.baseSearchQuery())
  const labelOptions = useMemo(
    () =>
      (labels ?? []).map((l) => ({
        label: <LabelOption color={l.color} label={l.name} />,
        value: l.reference.reference,
        icon: <LabelColor color={l.color} />,
        keywords: [l.name],
      })),
    [labels]
  )

  const filterOptions = useMemo<FilterConfigMap<StandardTask>>(
    () => ({
      status: {
        label: 'Status',
        icon: <IconProgress className="size-4" />,
        type: 'multi-select',
        options: [
          {
            label: (
              <div className="flex items-center gap-2">
                <IconTaskState state="todo" />
                <TaskStateText state="todo" />
              </div>
            ),
            icon: <IconTaskState state="todo" />,
            value: 'todo',
            keywords: ['todo', 'new', 'ready', 'to do'],
          },
          {
            label: (
              <div className="flex items-center gap-2">
                <IconTaskState state="in-progress" />
                <TaskStateText state="in-progress" />
              </div>
            ),
            icon: <IconTaskState state="in-progress" />,
            value: 'in-progress',
            keywords: ['in progress', 'ongoing', 'started', 'working'],
          },
          {
            label: (
              <div className="flex items-center gap-2">
                <IconTaskState state="done" />
                <TaskStateText state="done" />
              </div>
            ),
            icon: <IconTaskState state="done" />,
            value: 'done',
            keywords: ['done', 'completed', 'finished'],
          },
          {
            label: (
              <div className="flex items-center gap-2">
                <IconTaskState state="canceled" />
                <TaskStateText state="canceled" />
              </div>
            ),
            icon: <IconTaskState state="canceled" />,
            value: 'canceled',
            keywords: ['canceled', 'cancelled', 'stopped'],
          },
          {
            label: (
              <div className="flex items-center gap-2">
                <IconTaskState state="backlog" />
                <TaskStateText state="backlog" />
              </div>
            ),
            icon: <IconTaskState state="backlog" />,
            value: 'backlog',
            keywords: ['backlog', 'later', 'queued'],
          },
          {
            label: (
              <div className="flex items-center gap-2">
                <IconTaskState state="duplicate" />
                <TaskStateText state="duplicate" />
              </div>
            ),
            icon: <IconTaskState state="duplicate" />,
            value: 'duplicate',
            keywords: ['duplicate', 'copy', 'repeated'],
          },
        ],
        valueFn: (task) => task.state,
      },
      priority: {
        label: 'Priority',
        icon: <TaskPriorityIcon priority="asap" />,
        type: 'multi-select',
        options: [
          {
            label: <TaskPriorityOption priority="stat" isSelected={false} />,
            icon: <TaskPriorityIcon priority="stat" />,
            value: 'stat',
            selectedLabel: `STAT: ${priorityDefinition('stat')}`,
            keywords: ['stat', 'urgent', 'STAT'],
          },
          {
            label: <TaskPriorityOption priority="urgent" isSelected={false} />,
            icon: <TaskPriorityIcon priority="urgent" />,
            value: 'urgent',
            selectedLabel: `High: ${priorityDefinition('urgent')}`,
            keywords: ['high'],
          },
          {
            label: <TaskPriorityOption priority="asap" isSelected={false} />,
            icon: <TaskPriorityIcon priority="asap" />,
            value: 'asap',
            selectedLabel: `Medium: ${priorityDefinition('asap')}`,
            keywords: ['medium'],
          },
          {
            label: <TaskPriorityOption priority="routine" isSelected={false} />,
            icon: <TaskPriorityIcon priority="routine" />,
            value: 'routine',
            selectedLabel: `Low: ${priorityDefinition('routine')}`,
            keywords: ['low'],
          },
          {
            label: (
              <div className="flex items-center gap-2">
                <TaskPriorityIcon priority={undefined} />
                No Priority
              </div>
            ),
            icon: <TaskPriorityIcon priority={undefined} />,
            value: '',
            keywords: ['none', 'no priority'],
          },
        ],
        valueFn: (task) => task.priority ?? '',
      },
      owner: {
        label: 'Owner',
        icon: <IconUser className="size-4" />,
        type: 'multi-select',
        options: ownerOptions,
        valueFn: (task) => task.ownerPractitioner?.reference ?? '',
      },
      requester: {
        label: 'Requester',
        icon: <IconUser className="size-4" />,
        type: 'multi-select',
        options: practitionerOptions,
        valueFn: (task) => task.requesterPractitioner?.reference ?? '',
      },
      roles: {
        label: 'Roles',
        icon: <IconUser className="size-4" />,
        type: 'multi-select-array',
        options: ROLE_OPTIONS_WITH_KEYWORDS,
        valueFn: (task) => compact(task.roles),
      },
      labels: {
        label: 'Labels',
        icon: <IconLabel />,
        type: 'multi-select-array',
        options: labelOptions,
        valueFn: (task) => compact(task.labels?.map((l) => l.reference)),
      },
      participant: {
        label: 'Participant',
        icon: <IconUser className="size-4" />,
        type: 'multi-select',
        options: participantOptions,
        valueFn: (task) => task.participant?.reference ?? '',
      },
      participantStatus: {
        label: 'Participant Status',
        icon: <IconUser className="size-4" />,
        type: 'multi-select',
        options: [
          {
            label: (
              <div className="flex items-center gap-2">
                <LabelColor color="lch(48% 59.31 288.43)" />
                Active
              </div>
            ),
            icon: <LabelColor color="lch(48% 59.31 288.43)" />,
            value: 'active',
            keywords: ['active'],
          },
          {
            label: (
              <div className="flex items-center gap-2">
                <LabelColor color="#4CB782" />
                Lead
              </div>
            ),
            icon: <LabelColor color="#4CB782" />,
            value: 'lead',
            keywords: ['lead'],
          },
        ],
        valueFn: (task) => {
          const participant = participants.find(
            (p) => p.reference.reference === task.participant?.reference
          )
          if (!participant) {
            return ''
          }
          if (participant.isLead) {
            return 'lead'
          }
          if (participant.isActive) {
            return 'active'
          }
          return ''
        },
      },
      title: {
        label: 'Title',
        icon: <IconSearch className="size-4" />,
        type: 'text',
        valueFn: (task) => task.title ?? '',
      },
    }),
    [
      participantOptions,
      practitionerOptions,
      ownerOptions,
      labelOptions,
      participants,
    ]
  )
  const [selectedTasks, setSelectedTasks] = useState<Map<string, StandardTask>>(
    new Map()
  )

  const filteredTasks = useFilteredData(tasks, filterOptions, filters)

  const setTaskComposerOpen = useTaskComposerStore((state) => state.setOpen)

  const { data: recentSelections } = useRead(
    RecentSelections,
    currentPractitioner?.recentSelections
  )
  const patchRecentSelections = usePatchMutation<RecentSelections>(
    currentPractitioner?.recentSelections?.reference
      ? {
          mutation: {
            scope: {
              id: currentPractitioner.recentSelections.reference,
            },
          },
        }
      : undefined
  ).mutate

  const [composerOpen, setComposerOpen] = useState(false)
  const [editView, setEditView] = useState<TaskView | undefined>(undefined)

  useHotkeys('meta+a', (e) => {
    e.preventDefault()
    e.stopPropagation()

    if (selectedTasks.size === 0) {
      setSelectedTasks(
        new Map(filteredTasks?.map((task) => [task.id, task]) ?? [])
      )
    } else {
      setSelectedTasks(new Map())
    }
  })

  return (
    <div className={cn('relative flex flex-col', 'size-full print:size-auto')}>
      <Helmet title="Tasks" />
      <div>
        <ListHeader>
          <Button
            variant="outline"
            size="xs"
            selected={selectedView === 'all'}
            onClick={() => changeView('all', EMPTY_FILTER_VIEW, 'state')}
          >
            All tasks
          </Button>
          <Button
            variant="outline"
            size="xs"
            selected={selectedView === 'assigned'}
            onClick={() =>
              changeView('assigned', defaultAssignedFilters, 'state')
            }
          >
            Assigned to me
          </Button>
          <Button
            variant="outline"
            size="xs"
            selected={selectedView === 'created'}
            onClick={() =>
              changeView('created', defaultCreatedFilters, 'state')
            }
          >
            Created by me
          </Button>

          <ViewRow
            views={taskViews}
            selectedView={selectedView}
            onSelectView={(newViewReferenceString) => {
              const newView = taskViews?.find(
                (t) => t.referenceString === newViewReferenceString
              )
              invariant(newView, 'Task view not found')
              changeView(
                newViewReferenceString,
                JSON.parse(newView.filters),
                newView.grouping
              )
            }}
            onDeleteSelectedView={() =>
              changeView('assigned', EMPTY_FILTER_VIEW, 'state')
            }
          />
        </ListHeader>
        <div className="flex items-center justify-between gap-2 border-b-[0.5px] px-pagex-default py-2 lg:px-pagex-lg">
          <div className="flex items-center gap-2">
            <FilterEditor
              filterOptions={filterOptions}
              filters={filters}
              setFilters={setFilters}
            />
            <div className="flex items-center gap-1 text-2xs whitespace-nowrap text-foreground-secondary">
              {filteredTasks?.length ?? 0}{' '}
              {pluralize('task', filteredTasks?.length ?? 0)}
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-help">*</span>
                </TooltipTrigger>
                <TooltipContent>
                  Done, canceled, and duplicate tasks are excluded by default.
                  Filter by those statuses to include them.
                </TooltipContent>
              </Tooltip>
              {isLoading && <Spinner className="size-3.5" />}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Popover>
              <Tooltip>
                <TooltipTrigger asChild>
                  <PopoverTrigger asChild>
                    <DisplayButton
                      showIndicator={
                        isGroupingChanged || isSortDirty || isDisplayDirty
                      }
                    />
                  </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent>Display options</TooltipContent>
              </Tooltip>
              <PopoverContent className="w-[292px] p-0 py-2">
                {/* Offscreen focus trap prevents tooltips inside from auto-triggering on popover open */}
                <span
                  // oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- intentional focus trap
                  tabIndex={0}
                  style={{
                    position: 'absolute',
                    top: 9999,
                    right: 9999,
                    width: 0,
                    height: 0,
                  }}
                />
                <div className="flex flex-col gap-2">
                  <div className="flex flex-col px-2">
                    <div className="flex items-center gap-4 px-4 py-1">
                      <span className="w-16 shrink-0 text-sm font-medium">
                        Sorting
                      </span>
                      <div className="flex min-w-0 flex-1 gap-1">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="icon"
                              className="size-7 shrink-0"
                              onClick={() =>
                                updateCurrentSort((prev) => ({
                                  ...prev,
                                  direction:
                                    prev.direction === 'asc' ? 'desc' : 'asc',
                                }))
                              }
                            >
                              {sort.direction === 'asc' ? (
                                <IconSortAscending className="size-4" />
                              ) : (
                                <IconSortDescending className="size-4" />
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            {sort.direction === 'asc'
                              ? 'Ascending'
                              : 'Descending'}
                          </TooltipContent>
                        </Tooltip>
                        <Combobox
                          options={TASK_SORT_FIELDS}
                          onSelect={(field) =>
                            updateCurrentSort((prev) => ({
                              field,
                              direction: prev.direction,
                            }))
                          }
                        >
                          <button
                            type="button"
                            className="flex min-w-0 flex-1 items-center gap-1 rounded-md border border-border bg-background px-2 py-1.5"
                          >
                            <span className="flex-1 truncate text-left text-xs">
                              {TASK_SORT_FIELDS.find(
                                (f) => f.value === sort.field
                              )?.label ?? 'Field'}
                            </span>
                            <IconChevronDown className="size-4 shrink-0 text-muted-foreground" />
                          </button>
                        </Combobox>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 px-4 py-1">
                      <span className="w-16 shrink-0 text-sm font-medium">
                        Grouping
                      </span>
                      <div className="flex min-w-0 flex-1 gap-1">
                        <Combobox
                          options={[
                            {
                              label: (
                                <ComboboxOption
                                  isSelected={grouping === 'state'}
                                >
                                  Status
                                </ComboboxOption>
                              ),
                              value: 'state',
                              keywords: ['Status', 'Group by status'],
                            },
                            {
                              label: (
                                <ComboboxOption
                                  isSelected={grouping === 'participant'}
                                >
                                  Participant
                                </ComboboxOption>
                              ),
                              value: 'participant',
                              keywords: ['Participant', 'Group by participant'],
                            },
                          ]}
                          onSelect={(value) =>
                            updateCurrentGrouping(value as TaskListGrouping)
                          }
                        >
                          <button
                            type="button"
                            className="flex w-full items-center gap-1 rounded-md border border-border bg-background px-2 py-1.5"
                          >
                            <span className="flex-1 truncate text-left text-xs">
                              {grouping === 'state' && 'Status'}
                              {grouping === 'participant' && 'Participant'}
                            </span>
                            <IconChevronDown className="size-4 shrink-0 text-muted-foreground" />
                          </button>
                        </Combobox>
                      </div>
                    </div>
                  </div>
                  <div className="h-px bg-border" />
                  <div className="flex flex-col px-2">
                    <div className="px-1.5 pt-2">
                      <span className="text-[13px] leading-[18px] font-bold tracking-[0.52px] text-muted-foreground uppercase">
                        Display properties
                      </span>
                    </div>
                    {TASK_DISPLAY_PROPERTIES.map(({ key, label }) => (
                      <label
                        key={key}
                        className="flex cursor-pointer items-center gap-2 px-4 py-2"
                      >
                        <Checkbox
                          value={currentDisplay[key]}
                          onChange={() =>
                            setDisplay({
                              display: (d) => ({
                                ...d,
                                [key]: !currentDisplay[key],
                              }),
                              forView: selectedView,
                            })
                          }
                        />
                        <span className="text-sm font-medium">{label}</span>
                      </label>
                    ))}
                  </div>
                  {(isDisplayDirty || isSortDirty || isGroupingChanged) && (
                    <div className="flex justify-end border-t px-2 pt-2">
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => {
                          unsetDisplay(selectedView)
                          unsetSort()
                          updateCurrentGrouping(viewGrouping)
                        }}
                      >
                        Reset
                      </Button>
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>
            <div className="ml-2 h-4 w-[0.5px] bg-border" />
            <Button
              variant="ghost"
              size="xs"
              onClick={() => {
                updateCurrentFilters(viewFilters)
                updateCurrentGrouping(viewGrouping)
              }}
              disabled={!canResetFilters}
            >
              Reset
            </Button>
            <Dialog open={composerOpen} onOpenChange={setComposerOpen}>
              {!isReadOnly && (
                <DialogButton
                  variant="outline"
                  size="xs"
                  className="group transition"
                  onClick={() => setEditView(undefined)}
                  disabled={!canResetFilters}
                >
                  Save
                </DialogButton>
              )}
              <DialogDisplay>
                <NewViewDialogContent
                  filters={filters}
                  grouping={grouping}
                  onSubmit={(view) => {
                    setComposerOpen(false)
                    changeView(
                      view.referenceString,
                      JSON.parse(view.filters),
                      view.grouping
                    )
                    if (recentSelections) {
                      patchRecentSelections({
                        instance: recentSelections,
                        mutateFn: (rs) => {
                          rs.add(view.reference.reference)
                        },
                      })
                    }
                  }}
                  view={editView}
                />
              </DialogDisplay>
            </Dialog>
          </div>
        </div>
      </div>
      <div className="relative min-h-0 flex-1">
        {!isEmpty(filteredTasks) && (
          <VirtualizedTaskList
            tasks={filteredTasks}
            selectedTasks={selectedTasks}
            setSelectedTasks={setSelectedTasks}
            scrollRestorationId="tasks-page-all"
            grouping={grouping}
            sort={sort}
            display={currentDisplay}
          />
        )}
        {selectedTasks.size > 0 && (
          <BulkActions
            selectedTasks={selectedTasks}
            setSelectedTasks={setSelectedTasks}
          />
        )}
        {isEmpty(filteredTasks) && !isLoading && (
          <div className="px-pagex-default lg:px-pagex-lg">
            <h2 className="mt-6">All tasks</h2>
            <p className="mt-4 text-muted-foreground">There are no tasks.</p>
            {!isReadOnly && (
              <Button
                className="mt-8"
                onClick={() => setTaskComposerOpen(true)}
              >
                Create task
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function BulkActions({
  selectedTasks,
  setSelectedTasks,
}: {
  selectedTasks: Map<string, StandardTask>
  setSelectedTasks: (tasks: Map<string, StandardTask>) => void
}): ReactNode {
  const practitioners = usePractitionersInternal()
  const { isPending: isBatchPending, mutate: batchMutate } = useBatchMutation()
  const open = useConfirmationStore((state) => state.open)
  const markRelatedInboxItemsAsDone = useMarkRelatedInboxItemsAsDone({
    silent: true,
  })

  const applyStateChange = useCallback(
    (state: StandardTask['state']) => {
      const tasks = Array.from(selectedTasks.values())
      batchMutate(
        (batch) => {
          tasks.forEach((task) => {
            batch.patch(task, (t) => {
              t.state = state
            })
          })
        },
        {
          onSuccess: () => {
            markRelatedInboxItemsAsDone(
              createTaskFilter(
                tasks.map((task) => ({
                  taskReferenceString: task.reference.reference,
                  state,
                }))
              )
            )
            toast.success(
              `Updated ${tasks.length} ${pluralize('task', tasks.length)}.`
            )
          },
        }
      )
    },
    [batchMutate, markRelatedInboxItemsAsDone, selectedTasks]
  )

  return (
    <BulkActionBar
      count={selectedTasks.size}
      onClear={() => setSelectedTasks(new Map())}
    >
      <OwnerComboboxButton
        value={null}
        onChange={(owner) => {
          const practitioner = practitioners.find(
            (p) => p.reference.reference === owner
          )
          if (selectedTasks.size > 10) {
            open({
              title: practitioner
                ? `Assign the ${selectedTasks.size} selected tasks to ${practitioner.name}?`
                : `Unassign the ${selectedTasks.size} selected tasks?`,
              description: 'This action can be undone by contacting our team.',
              onConfirm: () =>
                batchMutate((batch) => {
                  selectedTasks.forEach((task) => {
                    batch.patch(task, (t) => {
                      t.ownerPractitioner = practitioner?.reference
                    })
                  })
                }),
            })
          } else {
            batchMutate((batch) => {
              selectedTasks.forEach((task) => {
                batch.patch(task, (t) => {
                  t.ownerPractitioner = practitioner?.reference
                })
              })
            })
          }
        }}
        disabled={isBatchPending}
      />
      <TaskStateCombobox
        selected="todo"
        onSelect={(state) => {
          if (selectedTasks.size > 10) {
            open({
              title: `Change the ${selectedTasks.size} selected tasks to ${StandardTask.stateText(state)}?`,
              description: 'This action can be undone by contacting our team.',
              onConfirm: () => applyStateChange(state),
            })
          } else {
            applyStateChange(state)
          }
        }}
      >
        <Button variant="outline" disabled={isBatchPending}>
          <IconProgress className="mr-2 size-4" /> Status
        </Button>
      </TaskStateCombobox>
    </BulkActionBar>
  )
}

interface TasksPageDisplay {
  showPriority: boolean
  showLabels: boolean
  showDueDate: boolean
  showAuthoredOn: boolean
  showOwner: boolean
}

const DEFAULT_DISPLAY: TasksPageDisplay = {
  showPriority: true,
  showLabels: true,
  showDueDate: true,
  showAuthoredOn: true,
  showOwner: true,
}

const DEFAULT_SORT: TaskListSort = { field: 'priority', direction: 'asc' }

const useTasksPageStore = create(
  devtools(
    persist(
      combine(
        {
          view: 'assigned' as TasksPageView,
          groupingByView: {} as Record<string, TaskListGrouping>,
          filtersByView: {} as Record<string, Filter[]>,
          displayByView: {} as Record<
            string,
            Partial<TasksPageDisplay> | undefined
          >,
          sort: DEFAULT_SORT as TaskListSort,
        },
        (set) => ({
          updateCurrentGrouping: (
            nextGrouping:
              | TaskListGrouping
              | ((currentGrouping: TaskListGrouping) => TaskListGrouping)
          ) => {
            set((state) => ({
              groupingByView: {
                ...state.groupingByView,
                [state.view]:
                  typeof nextGrouping === 'function'
                    ? nextGrouping(state.groupingByView[state.view])
                    : nextGrouping,
              },
            }))
          },
          updateCurrentFilters: (
            nextFilters: Filter[] | ((currentFilters: Filter[]) => Filter[])
          ) => {
            set((state) => ({
              filtersByView: {
                ...state.filtersByView,
                [state.view]:
                  typeof nextFilters === 'function'
                    ? nextFilters(state.filtersByView[state.view])
                    : nextFilters,
              },
            }))
          },
          updateCurrentSort: (
            nextSort:
              | TaskListSort
              | ((currentSort: TaskListSort) => TaskListSort)
          ) => {
            set((state) => ({
              sort:
                typeof nextSort === 'function'
                  ? nextSort(state.sort)
                  : nextSort,
            }))
          },
          unsetSort: () => set({ sort: DEFAULT_SORT }),
          setDisplay: ({
            display,
            forView,
          }: {
            display:
              | Partial<TasksPageDisplay>
              | ((
                  currentDisplay: Partial<TasksPageDisplay>
                ) => Partial<TasksPageDisplay>)
            forView: TasksPageView
          }) => {
            set((state) => ({
              displayByView: {
                ...state.displayByView,
                [forView]:
                  typeof display === 'function'
                    ? display(state.displayByView[forView] ?? {})
                    : display,
              },
            }))
          },
          unsetDisplay: (view: TasksPageView) => {
            set((state) => ({
              displayByView: omit(state.displayByView, view),
            }))
          },
          changeView: (
            nextView: TasksPageView,
            defaultFilters: Filter[],
            defaultGrouping: TaskListGrouping | undefined
          ) => {
            set((state) => {
              // Seed only when no entry has ever been written. `isEmpty` would
              // also match an explicitly-stored `[]` (user removed every
              // filter), causing tab navigation to silently restore defaults.
              const shouldUpdateFilters =
                state.filtersByView[nextView] === undefined
              const shouldUpdateGrouping =
                defaultGrouping && state.groupingByView[nextView] === undefined
              return {
                view: nextView,
                ...(shouldUpdateFilters && {
                  filtersByView: {
                    ...state.filtersByView,
                    [nextView]: defaultFilters,
                  },
                }),
                ...(shouldUpdateGrouping && {
                  groupingByView: {
                    ...state.groupingByView,
                    [nextView]: defaultGrouping,
                  },
                }),
              }
            })
          },
        })
      ),
      {
        name: 'tasks-page-view',
      }
    )
  )
)

function VirtualizedTaskList({
  tasks,
  selectedTasks,
  setSelectedTasks,
  scrollRestorationId,
  grouping,
  sort,
  display,
}: {
  tasks: StandardTask[]
  selectedTasks: Map<string, StandardTask>
  setSelectedTasks: (tasks: Map<string, StandardTask>) => void
  scrollRestorationId: string
  grouping: TaskListGrouping
  sort?: TaskListSort
  display?: TasksPageDisplay
}): ReactNode {
  const items = useTaskListItems(tasks, { grouping, sort })
  const [lastSelectedTaskId, setLastSelectedTaskId] = useState<string | null>(
    null
  )
  if (selectedTasks.size === 0 && lastSelectedTaskId) {
    setLastSelectedTaskId(null)
  }

  const handleTaskCheckedChange = useCallback(
    (task: StandardTask, value: boolean, isShiftClick = false) => {
      const newMap = new Map(selectedTasks)

      if (isShiftClick && lastSelectedTaskId && items) {
        // Find indices of last selected and current task in the visual list
        const lastIndex = items.findIndex(
          (item) => item.type === 'task' && item.task.id === lastSelectedTaskId
        )
        const currentIndex = items.findIndex(
          (item) => item.type === 'task' && item.task.id === task.id
        )

        if (lastIndex !== -1 && currentIndex !== -1) {
          // Select all tasks between last selected and current (inclusive)
          const startIndex = Math.min(lastIndex, currentIndex)
          const endIndex = Math.max(lastIndex, currentIndex)

          for (let i = startIndex; i <= endIndex; i++) {
            const item = items[i]
            // Only select actual tasks, not group headers
            if (item.type === 'task') {
              newMap.set(item.task.id, item.task)
            }
          }
        }
      } else {
        // Normal single selection
        if (value) {
          newMap.set(task.id, task)
        } else {
          newMap.delete(task.id)
        }
      }

      setSelectedTasks(newMap)

      // Update last selected task for shift+click functionality
      if (value) {
        setLastSelectedTaskId(task.id)
      }
    },
    [lastSelectedTaskId, items, selectedTasks, setSelectedTasks]
  )

  const isGroupHeader = useCallback(
    (index: number) => {
      const item = items[index]
      return item?.type === 'state-group' || item?.type === 'participant-group'
    },
    [items]
  )

  const rows = useMemo(
    () =>
      items.map((item) => (
        <TaskListRow
          key={item.key}
          item={item}
          tasks={tasks}
          selectedTasks={selectedTasks}
          setSelectedTasks={setSelectedTasks}
          grouping={grouping}
          display={display}
          onTaskCheckedChange={handleTaskCheckedChange}
        />
      )),
    [
      items,
      tasks,
      selectedTasks,
      setSelectedTasks,
      grouping,
      display,
      handleTaskCheckedChange,
    ]
  )

  return (
    <VirtualizedList
      rows={rows}
      estimateSize={GET_TASK_ROW_HEIGHT}
      scrollRestorationId={scrollRestorationId}
      isGroupHeader={isGroupHeader}
    />
  )
}

function GET_TASK_ROW_HEIGHT(): number {
  return 40
}

function NewViewDialogContent({
  filters,
  grouping,
  onSubmit,
  view,
}: {
  filters: Filter[]
  grouping: TaskListGrouping
  onSubmit: (view: TaskView) => any
  view?: TaskView
}): React.ReactNode {
  const createMutation = useCreateMutation<TaskView>()
  const patchMutation = usePatchMutation<TaskView>()
  const currentPractitioner = useCurrentPractitioner()

  const form = useForm({
    schema: z.object({
      name: z.string().min(1),
      visibility: z.enum(['private', 'shared']),
    }),
    defaultValues: {
      name: view?.name ?? '',
      visibility: view?.access === 'shared' ? 'shared' : 'private',
    },
  })

  const submit = form.handleSubmit((values) => {
    if (view) {
      patchMutation.mutate(
        {
          instance: view,
          mutateFn: (v) => {
            v.name = values.name
            v.access =
              values.visibility === 'shared'
                ? 'shared'
                : currentPractitioner.reference
            return v
          },
        },
        {
          onSuccess: onSubmit,
        }
      )
    } else {
      createMutation.mutate(
        new TaskView({
          name: values.name,
          owner: currentPractitioner.reference,
          filters: JSON.stringify(filters),
          grouping,
          access:
            values.visibility === 'shared'
              ? 'shared'
              : currentPractitioner.reference,
        }),
        {
          onSuccess: onSubmit,
        }
      )
    }
  })

  return (
    <Form onSubmit={submit}>
      <DialogHeader className="flex flex-row-reverse items-center py-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <RichTextEditor
              placeholder="View name"
              variant="unstyled"
              className="flex-1"
              contentClassName="text-xl font-bold"
              {...field}
            />
          )}
        />
      </DialogHeader>
      <DialogFooter>
        <FormField
          control={form.control}
          name="visibility"
          render={({ field: { value, onChange, ...rest } }) => (
            <div className="flex items-center gap-2">
              <TypographySubtle className="text-foreground-secondary">
                Save to
              </TypographySubtle>
              <Combobox
                options={[
                  {
                    label: (
                      <ComboboxOption isSelected={value === 'private'}>
                        <IconEye className="size-4 shrink-0" />
                        Private
                      </ComboboxOption>
                    ),
                    value: 'private',
                    keywords: ['Private'],
                  },
                  {
                    label: (
                      <ComboboxOption isSelected={value === 'shared'}>
                        <IconBuilding className="size-4 shrink-0" />
                        Organization
                      </ComboboxOption>
                    ),
                    value: 'shared',
                    keywords: ['Shared'],
                  },
                ]}
                onSelect={onChange}
              >
                <Button variant="outline" className="gap-2" {...rest}>
                  {value === 'private' && (
                    <>
                      <IconEye className="size-4 shrink-0" />
                      Private
                    </>
                  )}
                  {value === 'shared' && (
                    <>
                      <IconBuilding className="size-4 shrink-0" />
                      Organization
                    </>
                  )}
                </Button>
              </Combobox>
            </div>
          )}
        />
        <div className="my-1 w-[0.5px] self-stretch bg-border" />
        <DialogClose asChild>
          <Button variant="outline">Cancel</Button>
        </DialogClose>
        <SubmitButton
          submitting={createMutation.isPending || patchMutation.isPending}
        >
          {view ? 'Update view' : 'Create view'}
        </SubmitButton>
      </DialogFooter>
    </Form>
  )
}

type TasksPageView =
  | 'all'
  | 'assigned'
  | 'created'
  | TaskView['referenceString']
